import { IconBan, IconRotateClockwise, IconStar, IconStarFilled } from "@tabler/icons-react";
import { Badge, Button, Checkbox, Group, IconAction, Td, Text, Tr, useConfirmDialog } from "@/components/ui";
import type { ClaveSatCentroCosto } from "../services/clave-sat-centro-costo-service";
import {
  useCambiarEstadoClaveSatCentroCosto,
  useEstablecerClaveSatCentroCostoAutomatico,
} from "../hooks/use-clave-sat-centro-costo-mutations";

/**
 * Fila de la relación · muestra si el centro de costo es el AUTOMÁTICO de su clave y sociedad y
 * permite elegirlo (solo entre candidatos activos). Baja lógica en vez de eliminar fila a fila;
 * la eliminación definitiva es la masiva.
 */
export function ClaveSatCentroCostoRow({
  relacion,
  seleccionada,
  onToggleSeleccion,
}: {
  relacion: ClaveSatCentroCosto;
  seleccionada: boolean;
  onToggleSeleccion: () => void;
}) {
  const confirm = useConfirmDialog();
  const cambiarEstado = useCambiarEstadoClaveSatCentroCosto();
  const establecer = useEstablecerClaveSatCentroCostoAutomatico();

  const confirmarBaja = async () => {
    const ok = await confirm({
      title: `Dar de baja "${relacion.claveSat}" · ${relacion.centroCodigo}`,
      message: relacion.esAutomatico
        ? "Este es el centro de costo AUTOMÁTICO de la clave en esta sociedad. Al darlo de baja, la bandera pasa al siguiente por código (o la clave queda sin automático si no hay más candidatos). Puedes reactivarlo después."
        : "Deja de ser candidato de esta clave en esta sociedad. Los prorrateos históricos conservan su centro de costo. Puedes reactivarlo después.",
      confirmLabel: "Dar de baja",
      confirmColor: "peligro",
      icon: <IconBan size={14} />,
    });
    if (ok) cambiarEstado.mutate({ id: relacion.id, claveSat: relacion.claveSat, activo: false });
  };

  const puedeElegir = relacion.activo && !relacion.esAutomatico && relacion.candidatos > 1;

  return (
    <Tr>
      <Td>
        <Checkbox
          checked={seleccionada}
          onCheckedChange={() => onToggleSeleccion()}
          aria-label={`Seleccionar ${relacion.claveSat} · ${relacion.centroCodigo}`}
        />
      </Td>
      <Td>
        <Text fw={600} size="sm" ff="mono">
          {relacion.claveSat}
        </Text>
      </Td>
      <Td>
        <Text size="sm" c="dimmed">
          {relacion.claveSatDescripcion ?? "—"}
        </Text>
      </Td>
      <Td>
        <Text size="sm">
          <span className="font-mono">{relacion.numeroSociedad}</span> · {relacion.sociedadNombre || "—"}
        </Text>
      </Td>
      <Td>
        <Text size="sm">
          <span className="font-mono">{relacion.centroCodigo}</span> · {relacion.centroNombre}
        </Text>
      </Td>
      <Td>
        {relacion.esAutomatico ? (
          <Badge size="sm" color="exito">
            <IconStarFilled size={12} /> Automático
          </Badge>
        ) : puedeElegir ? (
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconStar size={12} />}
            loading={establecer.isPending}
            onClick={() =>
              establecer.mutate({ id: relacion.id, claveSat: relacion.claveSat, centroCodigo: relacion.centroCodigo })
            }
          >
            Hacer automático
          </Button>
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        )}
      </Td>
      <Td>
        <Badge size="sm" color={relacion.activo ? "exito" : "slate"}>
          {relacion.activo ? "Activo" : "Inactivo"}
        </Badge>
      </Td>
      <Td align="right">
        {/* Sin «Editar»: la relación no tiene campos editables (se da de baja y se crea otra). */}
        <Group gap="xs" justify="flex-end">
          {relacion.activo ? (
            <IconAction label="Dar de baja" variant="subtle" iconColor="peligro-600" onClick={confirmarBaja} loading={cambiarEstado.isPending}>
              <IconBan size={14} />
            </IconAction>
          ) : (
            <IconAction
              label="Reactivar"
              variant="subtle"
              iconColor="exito-600"
              onClick={() => cambiarEstado.mutate({ id: relacion.id, claveSat: relacion.claveSat, activo: true })}
              loading={cambiarEstado.isPending}
            >
              <IconRotateClockwise size={14} />
            </IconAction>
          )}
        </Group>
      </Td>
    </Tr>
  );
}
